import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Center,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck, IconTruckLoading } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { apiClient } from '../api/http';
import { useEmpresas, useCreateEmpresa } from '../api/hooks';
import { useLocalAuth } from '../auth/local-auth';

/**
 * Wizard post-login para crear la PRIMERA empresa del tenant.
 *
 * - Si al hacer login el user no tiene ninguna empresa en su tenant, el
 *   `Login` lo redirige aqui.
 * - El user puede saltarse este paso (boton "Mas tarde") y entrar al
 *   dashboard. La proxima vez que intente crear un envio/DeCA, el
 *   sistema le pedira una (futuro).
 * - El tipo por defecto es TRANSPORTISTA (autonomo tipico). Se permite
 *   cambiar a CARGADOR o AMBOS desde el selector.
 *
 * Los datos fiscales del Tenant (NIF, direccion, etc.) son del TITULAR
 * de la cuenta — esto es la ficha de la transportista/cargador que
 * opera DENTRO de ese tenant. Pueden diferir (p.ej. un grupo con
 * varias filiales).
 */
export default function OnboardingEmpresa() {
  const navigate = useNavigate();
  const { user } = useLocalAuth();
  const { data: empresas = [], isLoading: loadingEmpresas } = useEmpresas();
  const crear = useCreateEmpresa();
  const [submitting, setSubmitting] = useState(false);
  const [skipping] = useState(false);

  // Si ya tiene empresa (p.ej. el user entro desde /onboarding/empresa
  // por error tras login normal), redirigir al dashboard.
  useEffect(() => {
    if (!loadingEmpresas && empresas.length > 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [empresas.length, loadingEmpresas, navigate]);

  const form = useForm({
    initialValues: {
      tipo: 'TRANSPORTISTA' as 'TRANSPORTISTA' | 'CARGADOR' | 'AMBOS',
      razonSocial: '',
      nifCif: '',
      domicilioFiscal: '',
      municipio: '',
      provincia: '',
      codigoPostal: '',
      pais: 'ES',
      email: user?.email ?? '',
      telefono: '',
    },
    validate: {
      razonSocial: (v) => (v.trim() ? null : 'Requerido'),
      nifCif: (v) => (v.trim() ? null : 'Requerido'),
      codigoPostal: (v) => (!v.trim() || /^\d{4,5}$/.test(v.trim()) ? null : 'CP de 4 o 5 dígitos'),
    },
  });

  const submit = (values: typeof form.values) => {
    setSubmitting(true);
    crear.mutate(values, {
      onSuccess: () => {
        notifications.show({
          title: 'Empresa creada',
          message: `${values.razonSocial} ya esta disponible.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        // Invalidar cache (refresca la lista de empresas) y al dashboard.
        apiClient.get('/empresas').catch(() => undefined);
        navigate('/dashboard', { replace: true });
      },
      onError: (e: unknown) => {
        notifications.show({
          title: 'Error al crear la empresa',
          message: e instanceof Error ? e.message : 'Error desconocido',
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        });
        setSubmitting(false);
      },
    });
  };

  return (
    <Center mih="100vh" p="md" bg="gray.0">
      <Stack gap="lg" w="100%" maw={560}>
        <Stack gap="xs" align="center">
          <img src="/deca-logo.png" alt="DeCA" height={48} />
          <Title order={2}>Tu primera empresa</Title>
          <Text c="dimmed" size="sm" ta="center">
            Antes de empezar a emitir DeCAs, necesitas dar de alta al menos una empresa
            (transportista o cargador).
          </Text>
        </Stack>

        <Alert
          variant="light"
          color="deca"
          icon={<IconTruckLoading size={20} />}
          title="¿Por qué es necesaria?"
        >
          El DeCA identifica al transportista (y al cargador) responsable del envío. Sin una empresa
          registrada, no puedes crear envíos ni DeCAs. Los datos fiscales de tu{' '}
          <strong>cuenta SaaS</strong>
          se guardaron en el registro; estos son los datos de la
          <strong> empresa que opera</strong>, que pueden ser los mismos u otros.
        </Alert>

        <Card withBorder shadow="sm" padding="xl" radius="md">
          <form onSubmit={form.onSubmit(submit)}>
            <Stack gap="md">
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Tipo de empresa
                </Text>
                <SegmentedControl
                  fullWidth
                  color="deca"
                  data={[
                    { value: 'TRANSPORTISTA', label: 'Transportista' },
                    { value: 'CARGADOR', label: 'Cargador' },
                    { value: 'AMBOS', label: 'Ambos' },
                  ]}
                  {...form.getInputProps('tipo')}
                />
              </div>
              <Group grow>
                <TextInput
                  label="Razón social"
                  placeholder="Transportes López S.L."
                  withAsterisk
                  {...form.getInputProps('razonSocial')}
                />
                <TextInput
                  label="CIF / NIF"
                  placeholder="B12345678"
                  withAsterisk
                  {...form.getInputProps('nifCif')}
                  onChange={(e) => {
                    form.setFieldValue('nifCif', e.currentTarget.value.toUpperCase());
                  }}
                />
              </Group>
              <TextInput
                label="Domicilio fiscal"
                placeholder="Calle Mayor 1"
                {...form.getInputProps('domicilioFiscal')}
              />
              <Group grow>
                <TextInput
                  label="Código postal"
                  placeholder="28013"
                  {...form.getInputProps('codigoPostal')}
                />
                <TextInput
                  label="Población"
                  placeholder="Madrid"
                  {...form.getInputProps('municipio')}
                />
                <TextInput
                  label="Provincia"
                  placeholder="Madrid"
                  {...form.getInputProps('provincia')}
                />
              </Group>
              <Group grow>
                <Select
                  label="País"
                  data={[
                    { value: 'ES', label: 'España' },
                    { value: 'PT', label: 'Portugal' },
                    { value: 'FR', label: 'Francia' },
                  ]}
                  {...form.getInputProps('pais')}
                />
                <TextInput
                  label="Email"
                  placeholder="contacto@empresa.com"
                  {...form.getInputProps('email')}
                />
                <TextInput
                  label="Teléfono"
                  placeholder="+34 612 345 678"
                  {...form.getInputProps('telefono')}
                />
              </Group>
              <Group justify="space-between" mt="md">
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={() => navigate('/dashboard')}
                  disabled={submitting || skipping}
                >
                  Más tarde
                </Button>
                <Button color="deca" type="submit" loading={submitting} disabled={submitting}>
                  Crear empresa
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Center>
  );
}
